<template>
  <div class="gmf-img-grid-wrap" aria-label="Customer image gallery">
    <div class="gmf-img-grid">
      <div
      v-for="(panel, i) in layout" :key="i"
      class="gmf-img-grid-img" @click="commentId ? openPostModal(commentId, i) : openGallery(i)"
      :class="{
        'gmf-img-grid-img-more': i == layout.length-1 && images.length > layout.length
      }"
      :style="getStyle(panel, i)"><span v-if="i == layout.length-1 && images.length > layout.length" v-text="'+' + (images.length - layout.length)"/></div>
    </div>
  </div>
</template>

<style>
.gmf-img-grid-wrap {
  width: 100%;
  position: relative;
  border-radius: var(--gmf-rounding);
  overflow: hidden;
}
.gmf-img-grid-wrap:after {
  content: "";
  display: block;
  padding-top: 100%;
}
.gmf-img-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: 1fr;
  gap: var(--gmf-p-1);
  position: absolute;
  inset: 0;
}

.gmf-img-grid-img {
  background-position: center;
  background-size: cover;
  position: relative;
  cursor: pointer;
}
.gmf-img-grid-img-more:before {
  position: absolute;
  inset: 0;
  background: #111;
  content: "";
  opacity: 0.5;
}
.gmf-img-grid-img-more span {
  color: white;
  font-weight: bold;
  font-size: 36px;
  z-index: 1;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

</style>


<script>
import layouts from './layouts';

import modal from '@/utils/modal';
import { defineAsyncComponent } from 'vue';

const ImageGalleryModal = defineAsyncComponent(() => {
  return import(/* webpackChunkName: "stage-2" */'@/components/ImageGalleryModal');
});

const ratios = [
  {
    name: '9:16',
    ratio: 0.5625,
    index: 0,
  },
  {
    name: '3:4',
    ratio: 0.75,
    index: 1,
  },
  {
    name: '1:1',
    ratio: 1,
    index: 2,
  },
  {
    name: '4:3',
    ratio: 1.33,
    index: 3,
  },
  {
    name: '16:9',
    ratio: 1.77,
    index: 4,
  },
];

function getClosestRatio (fRatio) {
  let closest = {
    ratio: null,
    distance: 999,
  };
  for (const ratio of ratios) {
    const distance = Math.abs(ratio.ratio - fRatio);
    if (distance < closest.distance) {
      closest = {
        ratio,
        distance,
      };
    }
  }
  return closest.ratio;
}

function assignRatiosToLayouts (layouts) {
  const layoutsWithRatios = JSON.parse(JSON.stringify(layouts));
  for (const layout of layoutsWithRatios) {
    for (const img of layout) {
      let ratio = getClosestRatio(img.w/img.h);
      if (! ratio) {
        console.error('Could not find ratio for panel', img);
        ratio = ratios[2]; // 1:1
      }
      img.ratio = ratio;
    }
  }
  return layoutsWithRatios;
}

const layoutsWithRatios = assignRatiosToLayouts(layouts);

function layoutMatchScore (layout, imageRatios) {
  let score = 0;
  for (let i = 0; i < layout.length; ++i) {
    const l = layout[i].ratio;
    const r = imageRatios[i];

    const distance = 4 - Math.pow(1 - Math.abs(l.index - r.index), 2);

    if (i == 0) {
      score += distance * 3; // The first image is the most important
    } else {
      score += distance;
    }
  }
  return score;
}

import openPostModal from '@/widgets/PostModal/openPostModal';

export default {
  props: ['images', 'commentId'],
  computed: {
    imageRatios () {
      const ratios = [];
      for (const image of this.images) {
        let ratio = getClosestRatio(image.aspect);
        if (! ratio) {
          console.error('Could not find ratio for image', image);
          ratio = ratios[2]; // 1:1
        }
        ratios.push(ratio);
      }
      return ratios;
    },
    layout () {
      const imageRatios = this.imageRatios;

      const eligible = layoutsWithRatios.filter(l => {
        if (l.length == 1 && imageRatios.length > 1) { // If there are multiple images, we always need to show 2 or more
          return false;
        }
        return l.length <= this.images.length;
      });

      // @TODO if no eligible? is it even possible?

      return eligible.sort((l1, l2) => {
        return layoutMatchScore(l2, imageRatios) - layoutMatchScore(l1, imageRatios);
      })[0];
    },
  },
  methods: {
    getStyle(panel,i) {
      return {
        'grid-area': this.panelGridArea(panel),
        'background-image': 'url(' + this.images[i].url + ')',
        'padding-bottom': (panel.h/panel.w)*100 + '%', //safari image height fix
      };
    },
    openPostModal,
    openGallery (initialIndex) {
      modal.create(ImageGalleryModal, true, {
        provide: {
          initialIndex,
          images: this.images,
        },
      });
    },
    panelGridArea ({x,y,w,h}) {
      return (y+1)+'/'+(x+1)+'/'+(y+h+1)+'/'+(x+w+1);
    },
  },
};
</script>
